import { AddTimelineNoteButtonNode as AddTimelineNoteButtonSchema } from '@seen/core/tiptap/schema'
import { Button } from '@seen/ui'
import { IconPlus } from '@tabler/icons-react'
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { type JSX, memo, useCallback } from 'react'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'
import { useTimelineNoteComposerDialogStore } from '#src/hooks/useTimelineNoteComposerDialogStore.ts'

export const AddTimelineNoteButtonNode = AddTimelineNoteButtonSchema.extend({
  addNodeView() {
    return ReactNodeViewRenderer(AddTimelineNoteButtonNodeComponent)
  },
})

function AddTimelineNoteButtonNodeComponent(props: NodeViewProps): JSX.Element {
  return (
    <NodeViewWrapper className="inline-flex">
      <AddTimelineNoteButtonNodeComponentInner {...props} />
    </NodeViewWrapper>
  )
}

const AddTimelineNoteButtonNodeComponentInner = memo(
  function AddTimelineNoteButtonNodeComponentInner(
    props: NodeViewProps
  ): JSX.Element {
    const setTimelineNoteComposerDialogOpen =
      useTimelineNoteComposerDialogStore((state) => state.setOpen)

    const getPosRef = useCurrentRef(props.getPos)

    const handleClick = useCallback(() => {
      setTimelineNoteComposerDialogOpen(true, {
        onSuccess: (comment) => {
          const pos = getPosRef.current()
          if (pos === undefined) {
            return
          }
          props.editor
            .chain()
            .insertContentAt(pos, [
              {
                type: 'mention',
                attrs: {
                  reference: comment.reference.reference,
                },
              },
              { type: 'paragraph' },
            ])
            .setTextSelection(pos + 1)
            .focus()
            .run()
        },
      })
    }, [setTimelineNoteComposerDialogOpen, props.editor, getPosRef])

    return (
      <Button
        variant="unstyled"
        size="unstyled"
        className="inline-flex items-center gap-1 rounded-sm border-[0.5px] border-gray-450 bg-gray-470 px-1 py-px hover:bg-gray-460"
        disabled={!props.editor.isEditable}
        onClick={handleClick}
      >
        <IconPlus className="size-3.5 shrink-0 text-foreground-secondary" />
        <span className="text-foreground-tertiary">New chart note…</span>
      </Button>
    )
  }
)
